import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { User } from '../../../types';
import { getMediaUrl } from '../../../utils/media';
import { Card, CardHeader, CardContent, CardFooter } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Calendar, User as UserIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar'; // Assuming Shadcn Avatar exists

interface MemberCardProps {
    member: User;
}

export const MemberCard: React.FC<MemberCardProps> = ({ member }) => {
    const { t } = useTranslation('common');
    const avatarUrl = getMediaUrl(member.avatar);

    const getRoleBadge = (role: string) => {
        switch (role) {
            case 'instructor':
                return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200">{t('members.filters.instructor')}</Badge>;
            case 'admin':
                return <Badge className="bg-red-100 text-red-700 hover:bg-red-200">{t('members.filters.admin')}</Badge>;
            default:
                return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">{t('members.filters.student')}</Badge>;
        }
    };

    return (
        <Card className="hover:shadow-md transition-shadow duration-200 flex flex-col h-full bg-white border-gray-200">
            <CardContent className="pt-6 flex flex-col items-center text-center flex-grow">
                <div className="mb-4 relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-gray-50 bg-gray-100 flex items-center justify-center text-3xl font-bold text-gray-400">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt={member.full_name || member.username} className="w-full h-full object-cover" />
                        ) : (
                            (member.full_name || member.username || 'U')[0].toUpperCase()
                        )}
                    </div>
                    <div className="absolute bottom-0 right-0">
                        {/* Online indicator or something could go here */}
                    </div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                    {member.full_name || member.username}
                </h3>

                <div className="mb-3">
                    {getRoleBadge(member.role)}
                </div>

                {member.bio && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                        {member.bio}
                    </p>
                )}
            </CardContent>

            <CardFooter className="bg-gray-50/50 p-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
                <div className="flex items-center gap-1" title={t('members.card.joined', { date: '' }).replace('To:', '').trim()}>
                    <Calendar size={14} />
                    {member.created_at ? t('members.card.joined', { date: new Date(member.created_at).toLocaleDateString('vi-VN') }) : '-'}
                </div>
                <Link
                    to={`/profile/${member.username}`}
                    className="text-blue-600 font-medium hover:underline flex items-center gap-1"
                >
                    <UserIcon size={14} />
                    {t('members.card.viewProfile')}
                </Link>
            </CardFooter>
        </Card>
    );
};
